/**
 * Wire types for the migrations PORTFOLIO PASS.
 *
 * Distinct from the per-plan engine (`services/migrations/engine`), which compiles one
 * named component. The portfolio pass is org-wide triage: it discovers every
 * WorkflowRule, compiles each, runs check-only deploys, and clusters the results so a
 * reader sees "your 370 rules fall into 12 shapes" instead of 370 separate reports.
 *
 * TWO FACTS SHAPE EVERY TYPE BELOW.
 *
 *  1. THE INVENTORY IS FREE AND THE PASS IS NOT. `GET /inventory` answers for an org that
 *     has bought nothing; the other four routes are entitlement-gated and refuse with a
 *     403. The refusal body and the inventory's `triage` block are THE SAME SHAPE on
 *     purpose, so one gate type reads both and one screen renders both — see
 *     `lib/migrations/portfolio.ts`.
 *
 *  2. EVERY COUNT FAILS OPEN INTO "UNKNOWN", NEVER INTO ZERO. `counts.unaddressable` is
 *     the number of rules a pass could NOT be built over. A missing field rendered as `0`
 *     would be a claim that nothing is out of reach, which is the single most flattering
 *     thing this surface could get wrong on a sales call. `PORTFOLIO_COUNT_UNKNOWN` is
 *     the same device the engine uses for `undeclared_count`, for the same reason.
 */

/**
 * A count the server did not send. `-1` because it can never be a real count, and
 * because `null` would have to be threaded through every arithmetic site.
 */
export const PORTFOLIO_COUNT_UNKNOWN = -1;

/** The code the backend returns on every entitlement refusal, paid route or triage block. */
export const PORTFOLIO_NOT_ENTITLED = "PORTFOLIO_NOT_ENTITLED";

/**
 * A DEPLOYMENT STATE, and deliberately NOT `PORTFOLIO_NOT_ENTITLED`.
 *
 * `PLAYGROUND_MIGRATION_PORTFOLIO` is off, so this deployment runs no portfolio passes at
 * all — including ones created while it was on. The server's own note on the constant is
 * explicit that it "does not mean 'buy this'", and it carries NO `contact` field for the
 * same reason: there is nobody to call. An operator turns a flag on.
 *
 * Rendering it as the entitlement refusal would tell a customer who already paid to go and
 * buy something, which is both wrong and the kind of wrong that reaches an account team
 * instead of an operator. The two codes exist separately so a client can tell them apart,
 * and the only way to be sure it does is to name this one.
 */
export const PORTFOLIO_DISABLED = "PORTFOLIO_DISABLED";

/**
 * The org's scale.
 *
 * `workflow_rules` is THE TOTAL. `addressable` is a subset — the rules a pass could be
 * built over — and `unaddressable` is the remainder that could not. Rendering
 * `addressable` where a reader expects a total is the failure this comment exists to
 * prevent: it is always the larger-sounding, better-looking number.
 */
export interface PortfolioCounts {
  workflow_rules: number;
  objects: number;
  profiles: number;
  addressable: number;
  unaddressable: number;
}

/**
 * One row of the per-object breakdown, count-descending.
 *
 * `rules`, NOT `count` — `inventory.py::by_object()` emits `{"object": name, "rules": n}`.
 * This was `count` on first write, against a contract handed over as prose, and every row
 * of a real 69-object org would have been unreadable.
 */
export interface PortfolioObjectRow {
  object: string;
  rules: number;
}

/**
 * The per-object breakdown, plus a count of the rows this client could NOT read.
 *
 * THE UNREADABLE COUNTER EARNED ITS KEEP IMMEDIATELY. The element shape was the one part
 * of the contract given as prose rather than as fields, the guess (`count`) was wrong,
 * and the failure this produced was a panel saying "69 row(s) of the per-object breakdown
 * could not be read" — a one-line fix — instead of an empty table that reads as a small
 * org. It stays for the next field whose spelling drifts.
 */
export interface PortfolioByObject {
  rows: PortfolioObjectRow[];
  unreadable: number;
}

/**
 * Whether the paid pass may be offered, and what to do when it may not.
 *
 * Two shapes on the wire, both normalized into this one:
 *   entitled   → `{available: true, entitled: true}` and nothing else
 *   not        → `{available: false, entitled: false, code, message, reason, contact}`
 *
 * `contact` is the load-bearing field. A refusal that names no next step is a dead end,
 * and this refusal has one — the pass is bought, not begged for.
 */
export interface PortfolioTriage {
  available: boolean;
  entitled: boolean;
  code: string | null;
  message: string | null;
  reason: string | null;
  contact: string | null;
}

/** `GET /api/migrations/portfolio/inventory` — free, and the only route an unentitled org reaches. */
export interface PortfolioInventory {
  org_id: string;
  api_version: string;
  route: string;
  /**
   * Did the listing finish? `false` means every count is a FLOOR. Absent reads as
   * `false`, the same way the engine reads `source_complete`: a completeness claim the
   * server did not make is not one this client may make for it.
   */
  complete: boolean;
  pages_read: number;
  counts: PortfolioCounts;
  by_object: PortfolioByObject;
  triage: PortfolioTriage;
}

/**
 * `GET /estimate` — paid.
 *
 * `estimate` has no documented field list, so it is carried as the record the server
 * sent and rendered generically (`humanize(key)`: value) rather than interpreted. A
 * client that invented labels for a price breakdown would be putting words in the
 * seller's mouth.
 */
export interface PortfolioEstimate {
  org_id: string;
  scope: string;
  counts: PortfolioCounts;
  inventory_sha: string;
  estimate: Record<string, unknown>;
}

/** `POST /passes` body. `accept_estimate` is a STRICT boolean server-side — see the service. */
export interface PortfolioPassRequest {
  scope: string;
  component_count: number;
  accept_estimate: boolean;
  inventory_sha?: string;
  title?: string;
}

export interface PortfolioPassCreated {
  plan_id: string;
  status: string;
}

export interface PortfolioPassRunResult {
  plan_id: string;
  status: string;
}

/**
 * One cluster of the triage result: a SHAPE, not a rule.
 *
 * The cluster element's fields are undocumented, so `label`/`count` are read where
 * present and every other scalar the server sent is carried through as a labelled pair.
 * Nothing is dropped for not being recognised — an unrecognised field on the one screen
 * that explains what a customer bought is worth showing verbatim.
 */
export interface PortfolioCluster {
  key: string;
  label: string;
  count: number;
  known: boolean;
  fields: Array<{ label: string; value: string }>;
}

export interface PortfolioPassTriage {
  plan_id: string;
  status: string;
  counts: Record<string, number>;
  clusters: PortfolioCluster[];
}
